import {
  campaignFromAttribution, campaignFromUrl, getCampaign, setCampaign,
} from 'common/utils/partnerCampaign';
import React, { useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import { captureError } from 'common/utils/sentry';
import { syncAndroidPurchases } from 'features/pricing/syncPurchases';
import 'app.css';
import { Provider, useSelector, useDispatch } from 'react-redux';
import { BiometricAuth } from '@aparajita/capacitor-biometric-auth';
import { withBiometricPrompt } from 'common/utils/biometricGate';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { useGetProfilePictureQuery } from 'features/Sharing/SharingService';
import { PersistGate } from 'redux-persist/integration/react';
import { persistor, store } from 'common/store/store';
import { Purchases } from '@revenuecat/purchases-capacitor';
import { Purchases as PurchasesWeb } from '@revenuecat/purchases-js';
import WebRoutes from 'features/Routes/routes';
import { Capacitor } from '@capacitor/core';
import { setupIonicReact } from '@ionic/react';
import { Preferences } from '@capacitor/preferences';
import {
  Adjust, AdjustAttribution, AdjustConfig, AdjustEnvironment, AdjustLogLevel,
} from '@awesome-cordova-plugins/adjust';
import { ResponsiveProvider } from 'common/context';
import { LockScreen, WebLifecycleHandler } from 'common/components';
import {
  clearAutoUnlock, selectAutoUnlockRequested, selectIsLocked, selectShowSetupPrompt, unlockSession,
} from 'common/store/lockSlice';
import { clearCredentials } from 'features/login/authSlice';
import type { RootState } from 'common/store/store';
import mixpanel from 'mixpanel-browser';
import { Mixpanel } from 'common/utils/mixpanel';
import { initOneSignal } from 'common/utils/onesignal';
import { attachOneSignalClickListener } from 'common/utils/onesignalClicks';
import { syncMixpanelIdToRevenueCat } from 'common/utils/revenuecatMixpanel';
import { setSubscriptionStatus } from 'features/login/revenueCatSlice';
import GuestAnalyticsSync from 'features/GuestSession/GuestAnalyticsSync';
import { safeStorage } from 'common/utils/safeStorage';
import { registerAttributionRefresh } from 'common/utils/metaAttribution';

// The opaque panel that covers everything while a native session is locked.
//
// Native deliberately gets a cover rather than web's blur. Blurred text is
// still often readable, and this exact screen is what sits behind the iOS
// Face ID sheet - a tester picked up someone else's phone and could read
// their vaccination records and insurance through the prompt before she had
// even dismissed it (869egfjpu). Nothing behind this may be legible.
//
// It also carries the only way forward, because the alternative to a button
// here is a user stuck behind an opaque screen if authentication fails for
// any reason other than a deliberate cancel.
const NativeLockCover: React.FC = () => {
  const dispatch = useDispatch();
  const [isAuthenticating, setIsAuthenticating] = React.useState(false);
  const authUser = useSelector((state: RootState) => state.auth.user);
  const { data: profilePictureData } = useGetProfilePictureQuery();

  // Same shape LockScreen reads, because the user object is stored both
  // nested and flat depending on which path wrote it.
  const firstName = authUser?.user?.first_name || authUser?.first_name || '';
  const email = authUser?.user?.email || authUser?.email || '';
  const profilePicture = profilePictureData?.data?.profile_picture || null;

  const attemptUnlock = async () => {
    if (isAuthenticating) return;
    setIsAuthenticating(true);
    try {
      await withBiometricPrompt(() => BiometricAuth.authenticate({
        reason: 'Unlock Doqit',
        cancelTitle: 'Cancel',
        allowDeviceCredential: true,
        iosFallbackTitle: 'Use passcode',
      }));
      dispatch(unlockSession());
    } catch {
      // Stay locked. A failed or cancelled unlock leaves the cover up, which
      // is the secure outcome; the user can try again or sign out below.
    } finally {
      setIsAuthenticating(false);
    }
  };

  const signOut = () => {
    // Unlock before clearing so the login screen isn't left behind the cover.
    dispatch(unlockSession());
    dispatch(clearCredentials());
  };

  // On the cold-start handoff from Welcome, ask for Face ID as soon as the
  // cover appears so the user is not left staring at an Unlock button. Any
  // other mount (going to the background, the Face ID sheet itself taking
  // the foreground, a manual lock) leaves the prompt to the resume path in
  // AppLifecycleHandler or to the button, so there is only ever one prompt
  // in flight.
  const autoUnlockRequested = useSelector((state: RootState) => selectAutoUnlockRequested(state));
  React.useEffect(() => {
    if (!autoUnlockRequested) return;
    dispatch(clearAutoUnlock());
    attemptUnlock();
  }, [autoUnlockRequested]);

  return (
    <div
      data-cy="native-lock-cover"
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center"
      style={{
        paddingTop: 'env(safe-area-inset-top, 0px)',
        background: 'linear-gradient(180deg, #E6E1EB 0%, #CDC4D6 100%)',
      }}
    >
      <div className="w-full max-w-sm px-4 flex flex-col items-center text-center">
        <div className="w-16 h-16 mb-4 rounded-full bg-white flex items-center justify-center shadow-lg">
          <LockClosedIcon className="w-8 h-8 text-doqit-button-primary" />
        </div>

        <div className="mb-4">
          {profilePicture ? (
            <img
              src={profilePicture}
              alt={firstName}
              className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg"
            />
          ) : (
            <div className="w-20 h-20 rounded-full bg-doqit-button-primary flex items-center justify-center text-white text-2xl font-bold shadow-lg">
              {firstName.charAt(0).toUpperCase() || email.charAt(0).toUpperCase()}
            </div>
          )}
        </div>

        <h2 className="text-xl font-bold text-doqit-purple mb-1">
          Session Locked
        </h2>
        <p className="text-doqit-purple-light mb-6">
          {firstName || email}
        </p>

        <button
          type="button"
          onClick={attemptUnlock}
          disabled={isAuthenticating}
          className="w-full py-3 rounded-full font-medium text-white bg-doqit-button-primary disabled:opacity-50"
        >
          {isAuthenticating ? 'Unlocking...' : 'Unlock'}
        </button>
        <button
          type="button"
          onClick={signOut}
          className="mt-4 font-medium text-doqit-button-primary"
        >
          Sign out
        </button>
      </div>
    </div>
  );
};

// Content wrapper that blurs when locked for security (web only)
const SecureContentWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isLocked = useSelector((state: RootState) => selectIsLocked(state));
  const showSetupPrompt = useSelector((state: RootState) => selectShowSetupPrompt(state));
  const isNative = Capacitor.isNativePlatform();

  const isSecured = isLocked || showSetupPrompt;
  // Web blurs. Native covers - see NativeLockCover above for why a blur is
  // not good enough there. The comment that used to sit here said native
  // "handles lock with their own biometric screen"; it did not. LockScreen
  // returns null on native and AppLifecycleHandler rendered nothing, so
  // native had no lock of any kind until this.
  const shouldBlur = !isNative && isSecured;
  const shouldCover = isNative && isSecured;

  let secureStyle: React.CSSProperties | undefined;
  if (shouldBlur) {
    secureStyle = { filter: 'blur(10px)', pointerEvents: 'none', userSelect: 'none' };
  } else if (shouldCover) {
    // Belt and braces with the cover: even one frame of a tap landing on real
    // content while locked is one too many.
    secureStyle = { pointerEvents: 'none', userSelect: 'none' };
  }

  return (
    <>
      <div style={secureStyle} aria-hidden={shouldBlur || shouldCover}>
        {children}
      </div>
      {shouldCover && <NativeLockCover />}
    </>
  );
};

// Simple fallback UI for error boundary
export const ErrorFallback = () => (
  <div
    style={{
      padding: 20,
      textAlign: 'center',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    }}
  >
    <h2>Something went wrong</h2>
    <p>Please try refreshing the app.</p>
    <button
      type="button"
      onClick={() => window.location.reload()}
      style={{
        marginTop: 20,
        padding: '10px 20px',
        backgroundColor: '#AB72ED',
        color: '#fff',
        border: 'none',
        borderRadius: 8,
        cursor: 'pointer',
      }}
    >
      Refresh App
    </button>
  </div>
);
require('moment-precise-range-plugin');

const App = () => {
  setupIonicReact();
  useEffect(() => {
    // Align Mixpanel's distinct_id with the RevenueCat app-user id, but ONLY
    // for authenticated users. Signed out, guest session or not, Mixpanel's
    // identity is its own anonymous $device id (guests are never identified;
    // the guest token rides along as the guest_token super property, and
    // sign-up aliases the device id to the account); running this for those
    // states made boot fight the device identity on every load.
    const unifyIdentities = (rcAppUserID: string) => {
      try {
        const previousDistinctId = Mixpanel.getDistinctId();
        if (previousDistinctId !== rcAppUserID) {
          Mixpanel.identify(rcAppUserID);
        }
        Mixpanel.linkOneSignal(rcAppUserID);
        if (!safeStorage.getItem('id_unification_done')) {
          try {
            if (previousDistinctId && previousDistinctId !== rcAppUserID) {
              mixpanel.alias(rcAppUserID, previousDistinctId);
            }
            safeStorage.setItem('id_unification_done', '1');
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error('[id-unification] migration alias failed', e);
          }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[id-unification] identify failed', e);
      }
    };

    // Initialize RevenueCat when the app starts
    if (Capacitor.isNativePlatform()) {
      // Native: Use Capacitor SDK
      const revCatKey = Capacitor.getPlatform() === 'android'
        ? process.env.REACT_APP_REVENUECAT_ANDROID
        : process.env.REACT_APP_REVENUECAT_IOS;
      if (!revCatKey) {
        // This used to throw. A throw here is uncatchable by the boundary
        // below (App renders it, so it only covers App's CHILDREN, never
        // App's own effects), which meant a missing purchase key took the
        // whole app to a white screen. Payments not being configured must
        // never stop someone reaching their documents.
        captureError(new Error('RevenueCat API key is not defined'), {}, ['revenuecat-key-missing']);
        return;
      }
      Purchases.configure({ apiKey: revCatKey });
      Purchases.collectDeviceIdentifiers();
      // RevenueCat's Mixpanel integration must use the same distinct id as the
      // app, or its trial and purchase events land on a separate profile. Sync
      // now and after every later identify (see revenuecatMixpanel.ts).
      Mixpanel.setIdentifyListener(syncMixpanelIdToRevenueCat);
      syncMixpanelIdToRevenueCat(Mixpanel.getDistinctId());
      // Android: hand RevenueCat any Play purchase that never posted (app
      // killed mid-purchase, receipt rejected). Without this the entitlement
      // never appears and the hard paywall asks them to buy what they own.
      // Fire and forget; it never throws. No-op on iOS.
      syncAndroidPurchases('configure');
      // OneSignal (email + marketing push) shares RevenueCat's user id; no permission prompt here.
      initOneSignal();
      attachOneSignalClickListener();
      // Facebook anonymous ID -> RevenueCat $fbAnonId: sent further down, once the
      // App Tracking Transparency prompt has been answered (App Review wants the
      // prompt before anything that could be used for tracking, 2026-10-22).

      // Keep Redux entitlement state fresh whenever RevenueCat reports a change
      // (purchase, restore, cancellation, expiration — including changes the
      // user makes inside the Customer Center). Native only.
      Purchases.addCustomerInfoUpdateListener((customerInfo) => {
        try {
          const hasPremiumSubscription = !!customerInfo?.entitlements?.active?.['Doqit Pro'];
          store.dispatch(setSubscriptionStatus({ hasPremiumSubscription, customerInfo }));
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('[revenuecat] customer info update failed', e);
        }
      });

      // Only unify identities for an authenticated user (stored auth token).
      // For guests/anonymous the native RC id is a $RCAnonymousID: device id
      // that must not overwrite the Mixpanel guest_token / device identity.
      Preferences.get({ key: 'userToken' })
        .then(({ value }) => {
          if (!value) return;
          Purchases.getAppUserID()
            .then(({ appUserID }) => unifyIdentities(appUserID))
            .catch((e) => {
              // eslint-disable-next-line no-console
              console.error('[id-unification] native getAppUserID failed', e);
            });
        })
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.error('[id-unification] native token check failed', e);
        });

      const adjustConfig = new AdjustConfig(
        process.env.REACT_APP_ADJUST_SDK_TOKEN ?? '',
        AdjustEnvironment.Production,
      );
      adjustConfig.setLogLevel(AdjustLogLevel.Verbose);
      // Give the ATT prompt (fired after the notification prompt above) up to
      // 30s to be answered before Adjust reports the install, so the install
      // carries the IDFA when the user allows tracking.
      adjustConfig.setAttConsentWaitingInterval(30);

      // Where the install came from, onto the RevenueCat customer and the Mixpanel
      // profile. Adjust's answer changes after launch (it reports "No User Consent"
      // until the tracking prompt is answered, and Meta's campaign detail arrives
      // later still), so this listens for every update instead of reading once.
      // Reading once at launch is why ~90% of accounts were stuck on
      // "No User Consent" in RevenueCat (checked 2026-09-16).
      const saveAttribution = (attribution?: AdjustAttribution | null) => {
        if (!attribution) return;
        // An install from a partner's Adjust tracker tags them for the partner paywall.
        const partner = campaignFromAttribution(attribution.network, attribution.trackerName);
        if (partner && !getCampaign()) setCampaign(partner, 'adjust_attribution');
        Purchases.setAttributes({
          $mediaSource: attribution.network,
          // A partner tag owns $campaign: it is what the partner targeting rule reads.
          $campaign: getCampaign() ?? attribution.campaign,
          $adGroup: attribution.adgroup,
          $ad: attribution.creative,
          $creative: attribution.adid,
        }).catch(() => undefined);
        Mixpanel.setAttribution({
          ad_network: attribution.network,
          ad_campaign: attribution.campaign,
          ad_group: attribution.adgroup,
          ad_creative: attribution.creative,
          ad_tracker_name: attribution.trackerName,
        });
      };
      adjustConfig.setAttributionCallback(saveAttribution);
      // A partner link that went through the App Store (Adjust link with a deep link,
      // for someone who did not have the app yet) arrives here on first launch.
      adjustConfig.setDeferredDeeplinkCallback((deeplink: string) => {
        const campaign = campaignFromUrl(deeplink);
        if (campaign) setCampaign(campaign, 'adjust_deferred');
      });
      Adjust.initSdk(adjustConfig);

      Adjust.getAdid().then((adid) => {
        Purchases.setAdjustID({ adjustID: adid });
      });

      Adjust.getAttribution().then(saveAttribution);

      // The notification prompt no longer fires at boot (it now comes from the
      // reminder primer). The App Tracking Transparency chain runs from the
      // first onboarding screen and the first authed dashboard mount; hand it
      // the attribution callback so it can re-read after the prompt is answered.
      registerAttributionRefresh(() => Adjust.getAttribution().then(saveAttribution));
    } else {
      // Web: Use JavaScript SDK
      const webKey = process.env.REACT_APP_REVENUECAT_WEB;
      if (webKey) {
        const storedUserId = safeStorage.getItem('rc_user_id');
        if (storedUserId) {
          // Returning authenticated user — configure with real ID and unify
          // Mixpanel with it (guests/anonymous visitors skip unification, see
          // the comment on unifyIdentities above).
          PurchasesWeb.configure(webKey, storedUserId);
          unifyIdentities(storedUserId);
        } else {
          // New/anonymous user. IMPORTANT: an already-stored rc_anonymous_id is
          // reused verbatim whatever its format — rewriting it would orphan any
          // purchase already attached to it mid-flight. Only brand-new visitors
          // get a fresh id, generated in RevenueCat's own anonymous format
          // ($RCAnonymousID:<uuid>) so the first identifyUser()/logIn() after
          // signup performs a genuine anonymous→identified entitlement merge
          // (a custom-format id is treated as "identified" by RC and never
          // merges — see docs "Identifying Customers" login matrix).
          let anonymousId = safeStorage.getItem('rc_anonymous_id');
          if (!anonymousId) {
            anonymousId = PurchasesWeb.generateRevenueCatAnonymousAppUserId();
            safeStorage.setItem('rc_anonymous_id', anonymousId);
          }
          PurchasesWeb.configure(webKey, anonymousId);
        }
        // Same identity sync as native, for web trials and purchases.
        Mixpanel.setIdentifyListener(syncMixpanelIdToRevenueCat);
        syncMixpanelIdToRevenueCat(Mixpanel.getDistinctId());
      }
    }
  }, []);

  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <Provider store={store}>
        <PersistGate persistor={persistor}>
          <GuestAnalyticsSync />
          <ResponsiveProvider>
            <Toaster
              position="top-center"
              toastOptions={{ duration: 4000 }}
              containerStyle={{ top: 50 }}
            />
            {/* Web session lock - tracks activity and locks on inactivity */}
            <WebLifecycleHandler />
            {/* Lock screen overlay - shown when session is locked */}
            <LockScreen />
            {/* Content wrapper that blurs when locked for security */}
            <SecureContentWrapper>
              <WebRoutes />
            </SecureContentWrapper>
          </ResponsiveProvider>
        </PersistGate>
      </Provider>
    </Sentry.ErrorBoundary>
  );
};

export default App;
